import { useCallback, useEffect, useState } from 'react';
import { RuleManagerSettingsCache } from '../../lib/cache/SettingsCache';
import { SetupApiModel } from '../../../../lib/api/rulemanager/types';
import { useRuleManagerApi } from '../../../../lib/api/rulemanager/use-rulemanager-api';
import { P2GImageMessage } from '~/components/imagemessage';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';

interface props {
  children: React.ReactNode;
  overrideLoading?: React.ReactNode;
}

const SettingsWrapper = (props: props): JSX.Element => {
  const [loading, setLoading] = useState<'LOADING' | 'ERROR' | 'LOADED'>('LOADING');
  const { getSetup } = useRuleManagerApi();
  const { t } = useTranslation(TranslationNamespaces.RULEMANAGER);

  const onSettingsLoaded = useCallback((x: SetupApiModel) => {
    if (!x.smartSend.Name) {
      x.smartSend.Name = 'Smart Send';
    }
    RuleManagerSettingsCache.settings = { ...RuleManagerSettingsCache.settings, ...x };
  }, []);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;

    const begin = async () => {
      try {
        setLoading('LOADING');
        const settings = await getSetup();
        if (settings && settings.success && settings.result) {
          onSettingsLoaded(settings.result);
          setLoading('LOADED');
        } else {
          throw new Error('Failed to load settings');
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        setLoading('ERROR');
        timeoutId = setTimeout(begin, 5000);
      }
    };

    begin();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  if (loading === 'LOADING' || loading === 'ERROR') {
    if (props.overrideLoading) {
      return <>{props.overrideLoading}</>;
    }

    return (
      <P2GImageMessage
        image="/theme/smartsend/notifications/loading.png"
        message={t('LOADING', { defaultValue: 'Loading Rule Manager...' })}
        showLoader
        contentKey="image-rulemanager-loading"
      />
    );
  }

  return <>{props.children}</>;
};

export default SettingsWrapper;
